<template>
  <div>
    <div class="d-flex align-center">
      <h2>職員勤怠管理</h2>
      <v-spacer />
      <v-btn
        @click="exportMonthly"
        :loading="loading"
        color="primary"
        variant="flat"
        class="mr-4"
      >
        月次帳票出力
      </v-btn>
      <v-btn
        @click="exportDaily"
        :loading="loading"
        color="primary"
        variant="flat"
        class="mr-4"
      >
        日次帳票出力
      </v-btn>
      <v-btn
        @click="searchAttendance"
        :loading="loading"
        color="primary"
        variant="outlined"
      >
        <v-icon start>mdi-sync</v-icon>
        更新
      </v-btn>
    </div>

    <v-row>
      <date-pager
        v-model="searchFilters.date" class="pt-4"
        @input = "pageClick"
      ></date-pager>
    </v-row>

    <v-card variant="flat" class="mt-4 pa-2" color="grey-lighten-5">
      <v-row>
        <v-col cols="3">
          <h5>クラス</h5>
          <v-select
            v-model="searchFilters.classId"
            :items="classes"
            :item-title="(item) => `(${item.year}) ${item.className}`"
            item-value="classId"
            variant="outlined"
            hide-details="auto"
            density="compact"
            clearable
            :disabled="loading"
            color="primary"
          ></v-select>
        </v-col>
      </v-row>
    </v-card>

    <v-table class="pt-4" v-if="attendances">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">職員氏名</th>
            <th class="text-left">勤怠状況</th>
            <th class="text-left">担任クラス</th>
            <th class="text-left">出勤時刻</th>
            <th class="text-left">退勤時刻</th>
            <th class="text-left"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in attendances" :key="a.nurseryUser.userId">
            <td>{{ a.nurseryUser.lastName }} {{ a.nurseryUser.firstName }}</td>
            <td>
              {{ getStatusText(a.status) }}
            </td>
            <td>{{ getClassName(a.nurseryClasses) }}</td>
            <td>{{ a.attendedAt ? formatDate(a.attendedAt) : "-" }}</td>
            <td>{{ a.leftAt ? formatDate(a.leftAt) : "-" }}</td>
            <td>
              <v-btn
                color="primary"
                variant="outlined"
                small
                :disabled="loading"
                :to="`/staffAttendance/${a.nurseryUser.userId}/${searchFilters.date}/edit`"
              >
                編集
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import DatePager from "@/components/atoms/DatePager.vue";
import LocalDataService from "@/service/LocalDataService";
import { classModule } from "@/store/dataModules/classModule";
import { NurseryClassResponse, TimecardListResponse } from "chaild-api/lib";
import {
  ageItems,
  genderItems,
  statusItems,
  staffStatusItems,
} from "@/constants/attendance";
import dayjs from "dayjs";
import { staffAttandanceListModule } from "@/store/viewModules/timecard/staffAttendanceListModule";
import ApiTimecard from "@/api/ApiTimecard";

export default defineComponent({
  name: "StaffAttendanceList",

  components: {
    DatePager,
  },

  watch: {
    searchFilters: {
      deep: true,
      async handler() {
        await this.searchAttendance();
      },
    },
  },

  async mounted() {
    const staffAttendancePage = sessionStorage.getItem('staffAttendancePage');
    if (staffAttendancePage) this.searchFilters.date = staffAttendancePage;
    this.searchAttendance();
    this.listClasses();
  },

  data() {
    return {
      loading: false,
      searchFilters: {
        date: dayjs().format("YYYY-MM-DD"),
        age: "",
        classId: undefined,
        gender: "",
        status: "" as string,
        limit: 1000,
        skip: 0,
      },
      genderItems: genderItems,
      ageItems: ageItems,
      statusItems: statusItems,
    };
  },

  computed: {
    classes: function (): NurseryClassResponse[] {
      return classModule.classes;
    },
    attendances: function (): TimecardListResponse | null {
      const allAttendances = staffAttandanceListModule.attendances;

      if (!allAttendances) {
        return null;
      }

      // allAttendances が TimecardResponse[] 型と仮定
      const filteredAttendances = allAttendances.filter(attendance => {
        return attendance.nurseryUser && !attendance.nurseryUser.isHidden;
      });

      // フィルタリングされた配列そのものを返します
      return filteredAttendances;
    },
  },

  methods: {
    async pageClick(pageNumber) {
      if (pageNumber) sessionStorage.setItem('staffAttendancePage', String(pageNumber));
    },
    async listClasses() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule
          .listClass({
            nurseryId,
          })
          .catch();
      }
      this.loading = false;
    },
    async searchAttendance() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await staffAttandanceListModule.getAttendances({
          date: this.searchFilters.date,
          classId: this.searchFilters.classId || undefined,
        });
      }
      this.loading = false;
    },
    formatDate(date: string | null | undefined) {
      if (date) {
        return dayjs(date).format("HH:mm");
      }
      return null;
    },
    getClassName(classes: NurseryClassResponse[]) {
      return classes.map((c) => c.className).join(", ");
    },
    getAgeBasedonBirthdate(birthdate: string): number {
      const thisYear = dayjs();
      return thisYear.diff(birthdate, "year");
    },
    getStatusText(value: string): string {
      if (!value) {
        return "";
      }
      return staffStatusItems.find((s) => s.value == value)?.text || "";
    },
    async exportDaily() {
      const nurseryId = LocalDataService.getNurseryId();
      if (this.searchFilters.date && nurseryId) {
        this.loading = true;
        const response = await ApiTimecard.exportAttendance({
          date: this.searchFilters.date,
          nurseryId,
          fileType: "dailyCsv",
        });
        this.loading = false;

        if (response) {
          window.open(response.preSignedDownloadUrl, "_blank");
        }
      }
    },
    async exportMonthly() {
      const nurseryId = LocalDataService.getNurseryId();
      if (this.searchFilters.date && nurseryId) {
        this.loading = true;
        const response = await ApiTimecard.exportAttendance({
          date: this.searchFilters.date,
          nurseryId,
          fileType: "monthlyCsv",
        });
        this.loading = false;

        if (response) {
          window.open(response.preSignedDownloadUrl, "_blank");
        }
      }
    },
  },
});
</script>

<style>
.filter-caption {
  font-size: 0.9rem;
}
</style>
